import { formatWalletBalance } from './wallet-display';

export type StaticRewardPhpMirror = {
  my_sum?: string;
  team_sum?: string;
  mp?: string;
  user_level?: { id?: number; title?: string; alias?: string; price?: number | string } | string | null;
  mirror_imported_at?: string;
};

export type StaticRewardPreview = {
  user_id: number;
  principal_usd: string;
  exit_multiplier: string;
  cap_usd: string;
  cumulative_paid_usd: string;
  remaining_usd: string;
  star_package: string | null;
  reinvest_coin_type: string | null;
  php_mirror?: StaticRewardPhpMirror | null;
};

export type UoAssetStaticDisplay = {
  packageLabel: string;
  qtyLabel: string;
  forkLabel: string;
  paidLabel: string;
  capLabel: string;
  progressPct: number;
  exited: boolean;
};

function parseUserLevel(raw: StaticRewardPhpMirror['user_level']) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw) as { price?: number | string; title?: string; alias?: string };
  } catch {
    return null;
  }
}

function fmtUsdLabel(raw: string | number | null | undefined): string {
  if (raw == null || raw === '') return '—';
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 3 })}`;
}

/** Map static-reward preview → wallet UO asset panel + progress bar. */
export function mapStaticRewardPreview(preview: StaticRewardPreview | null | undefined): UoAssetStaticDisplay {
  if (!preview) {
    return {
      packageLabel: '—',
      qtyLabel: '0',
      forkLabel: '$0',
      paidLabel: '0',
      capLabel: '0',
      progressPct: 0,
      exited: false,
    };
  }

  const mirror = preview.php_mirror ?? null;
  const level = parseUserLevel(mirror?.user_level);
  const packageUsd = level?.price ?? preview.principal_usd;
  const paid = Number(preview.cumulative_paid_usd) || 0;
  const cap = Number(preview.cap_usd) || 0;
  const progressPct = cap > 0 ? Math.min(100, (paid / cap) * 100) : 0;
  const remaining = Number(preview.remaining_usd) || 0;

  return {
    packageLabel: fmtUsdLabel(packageUsd),
    qtyLabel: formatWalletBalance(mirror?.mp ?? '0'),
    forkLabel: fmtUsdLabel(preview.cumulative_paid_usd),
    paidLabel: formatWalletBalance(preview.cumulative_paid_usd),
    capLabel: formatWalletBalance(preview.cap_usd),
    progressPct,
    exited: cap > 0 && remaining <= 0,
  };
}
